import { useEffect, useRef, useState, type FormEvent } from 'react'
import { apiSend } from '../api/client'
import type { Envelope } from '../api/types'

type StatusProject = { id: string; name: string; lifecycle_state: string; row_version: number; permissions?: string[] }
export const projectStatusLabel = (status: string) => status === 'active' ? '进行中' : status === 'archived' ? '已归档' : '已移除'
export const canAdjustProjectStatus = (project: StatusProject) => ['active', 'archived'].includes(project.lifecycle_state) && Boolean(project.permissions?.includes(project.lifecycle_state === 'active' ? 'project.archive' : 'project.restore'))

export default function ProjectStatusDialog({ project, unsavedChanges = false, beforeChange, onSaved, onClose }: {
  project: StatusProject
  unsavedChanges?: boolean
  beforeChange?: () => Promise<unknown>
  onSaved: (status: string) => Promise<void> | void
  onClose: () => void
}) {
  const [status, setStatus] = useState(project.lifecycle_state)
  const [saving, setSaving] = useState(false), [error, setError] = useState('')
  const locked = useRef(false), modal = useRef<HTMLElement>(null)
  useEffect(() => {
    const previous = document.activeElement
    return () => { if (previous instanceof HTMLElement) previous.focus() }
  }, [])
  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (locked.current || status === project.lifecycle_state || !canAdjustProjectStatus(project)) return
    locked.current = true; setSaving(true); setError('')
    try {
      await beforeChange?.()
      const result = await apiSend<Envelope<StatusProject>>('PATCH', `/projects/${project.id}`, { lifecycle_state: status, expected_version: project.row_version })
      await onSaved(result.data.lifecycle_state)
    } catch (caught) { setError(caught instanceof Error ? caught.message : '状态调整失败，请重试') }
    finally { locked.current = false; setSaving(false) }
  }
  return <div className="modal-backdrop"><section ref={modal} className="modal project-status-dialog" role="dialog" aria-modal="true" aria-labelledby="project-status-title" onKeyDown={event => {
    if (event.key === 'Escape' && !saving) { event.stopPropagation(); onClose() }
    if (event.key === 'Tab') {
      const controls = modal.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled)')
      const first = controls?.[0], last = controls?.[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
  }}>
    <div className="modal-head"><h2 id="project-status-title">调整项目状态</h2><button type="button" className="btn ghost" disabled={saving} onClick={onClose}>关闭</button></div>
    <form onSubmit={save}><div className="project-status-body">
      <div className="project-status-current"><strong>{project.name}</strong><span>当前状态：{projectStatusLabel(project.lifecycle_state)}</span></div>
      <label className="field">调整为<select autoFocus aria-label="调整为" value={status} disabled={saving} onChange={event => setStatus(event.target.value)}><option value="active" disabled={project.lifecycle_state !== 'active' && !project.permissions?.includes('project.restore')}>进行中</option><option value="archived" disabled={project.lifecycle_state !== 'archived' && !project.permissions?.includes('project.archive')}>已归档</option></select></label>
      <p className="project-status-help">{status === 'archived' ? '归档后保留全部资料、需求和方案，可查看和下载；恢复为进行中后可继续编辑。' : '进行中的项目可继续维护资料、需求、方案和协作成员。'}</p>
      {unsavedChanges ? <p className="note warn">有未保存的修改，将先保存草稿，再调整项目状态。</p> : null}
      {error ? <p className="note danger" role="alert">{error}</p> : null}
    </div><div className="modal-foot"><button type="button" className="btn" disabled={saving} onClick={onClose}>取消</button><button type="submit" className="btn primary" disabled={saving || status === project.lifecycle_state || !canAdjustProjectStatus(project)}>{saving ? '保存中…' : unsavedChanges ? '保存草稿并调整' : '保存状态'}</button></div></form>
  </section></div>
}
